import {css, useTheme} from '@mui/material';
import * as React from 'react';
import {useEffect} from 'react';
import {GetStylesFunction} from 'src/js/common/types';
import {
  HierarchyTreeNode,
  HierarchyTreeNode as HierarchyTreeNodeModel,
} from 'src/js/model/hierarchy_tree_children';

import {HierarchyNodeContent} from './HierarchyNodeContent';
import {HierarchyNodeTick} from './HierarchyNodeTick';
import {HierarchySyncNodeChildren} from './HierarchySyncNodeChildren';

interface OwnProps {
  hierarchyId: string | number;
  hierarchyChildren?: HierarchyTreeNode[];
  isOpened?: boolean;
  isTogglingSeparated?: boolean;
  level?: number;
  node: HierarchyTreeNodeModel;
  onNodeClick?: (node: HierarchyTreeNodeModel) => void;
  showRoot?: boolean;
  srcNodeClick?: (node: HierarchyTreeNodeModel) => void;
  tabIndex?: number;
  toggleOpened?: (node: HierarchyTreeNodeModel) => void;
  highlightedValues?: string[];
  propIsItemHighlighted?: boolean;
  getOptionDisabled?: (row: any) => boolean;
}

type Props = OwnProps;

export const HierarchySyncNode: React.FC<Props> = (props) => {
  const {
    hierarchyChildren = [],
    hierarchyId,
    isOpened = false,
    isTogglingSeparated = false,
    level = 0,
    node,
    onNodeClick,
    showRoot = true,
    srcNodeClick,
    tabIndex = 0,
    toggleOpened,
    highlightedValues,
    propIsItemHighlighted,
    getOptionDisabled,
  } = props;

  const theme = useTheme();
  const styles = getStyles({theme});

  // open root on first render when its node should not be shown
  useEffect(() => {
    if (!showRoot && level === 1 && node && toggleOpened && !isOpened) {
      // open root
      toggleOpened(node);
    }
  }, [level, node, showRoot, isOpened, toggleOpened]);

  let isItemHighlighted = propIsItemHighlighted;
  const addedValue = String(node.getId());
  if (highlightedValues && highlightedValues.indexOf(addedValue) >= 0) {
    isItemHighlighted = true;
  }

  return (
    <div
      data-name={'hierarchy-sync-node--wrapper'}
      css={styles.dlHierarchySyncNode}
    >
      <li data-name={'hierarchy-sync-node--li-tree-item'} role={'treeitem'}>
        {showRoot && node ? (
          <div
            data-name={'hierarchy-sync-node--container'}
            css={styles.dlHierarchySyncNodeContainer}
          >
            <HierarchyNodeTick
              isOpened={isOpened}
              node={node}
              toggleOpened={toggleOpened}
              tabIndex={node.hasChildren() ? tabIndex : -1}
            />
            <HierarchyNodeContent
              isItemHighlighted={isItemHighlighted}
              node={node}
              onNodeClick={onNodeClick}
              tabIndex={tabIndex}
              getOptionDisabled={getOptionDisabled}
            />
          </div>
        ) : null}
      </li>
      <HierarchySyncNodeChildren
        hierarchyId={hierarchyId}
        hierarchyChildren={hierarchyChildren}
        isOpened={isOpened}
        isTogglingSeparated={isTogglingSeparated}
        level={level}
        srcNodeClick={srcNodeClick}
        tabIndex={tabIndex}
        highlightedValues={highlightedValues}
        getOptionDisabled={getOptionDisabled}
      />
    </div>
  );
};

HierarchySyncNode.displayName = 'HierarchySyncNode';

const getStyles: GetStylesFunction = (options) => ({
  dlHierarchySyncNode: css`
    width: 100%;
    list-style: none;
  `,
  dlHierarchySyncNodeContainer: css`
    display: flex;
    align-items: flex-start;
  `,
});
